import { ComponentRegistry, ComponentType } from './types-uischema';
import { readModeComponents } from './component-mappings/read';
import { editModeComponents } from './component-mappings/edit';
import { reportModeComponents } from './component-mappings/report';
import { embeddedReportModeComponents } from './component-mappings/embedded-report';
import { ControlType } from '.';
import { componentDefaults } from 'app/infrastructure/ui/component-configuration';
import { RenderMode } from 'app/types-shared';

/**
 * 
 * A mapping between a set of Configuration `control types` and the 
 * react component that should render
 * 
 */
export const registry: ComponentRegistry = {

  components: [

    /**
     *  a set of controls for read mode 
     */
    {
      mode: RenderMode.read,

      components: readModeComponents
    },

    /**
     * A set of controls for edit mode
     */
    {
      mode: RenderMode.edit,

      components: editModeComponents
    },

    {
      mode: RenderMode.report,
      parentMode: RenderMode.read,

      components: reportModeComponents
    },

    {
      mode: RenderMode.embeddedReport,
      parentMode: RenderMode.report,

      components: embeddedReportModeComponents
    }
  ]

};

export function* resolveRenderModes(mode: RenderMode): IterableIterator<RenderMode> {
  let currentMode = mode;
  while (currentMode === 0 || currentMode) {
    yield currentMode;
    currentMode = registry.components.find(s => s.mode === currentMode).parentMode;
  }
}

function resolveComponentForMode(typeId: number, mode: RenderMode): ComponentType {
  const componentsForMode = registry.components.find(s => s.mode === mode);
  const descriptor = componentsForMode.components[typeId];
  if (!descriptor && (componentsForMode.parentMode === 0 || componentsForMode.parentMode)) {
    return resolveComponentForMode(typeId, componentsForMode.parentMode);
  }
  return descriptor;
}

/**
 * Returns component and render metadata for given type and render mode 
 * 
 * @export
 * @param {string} typeString
 * @param {string} mode
 * @returns
 */
export function resolveComponent(typeId: number, mode: RenderMode): ComponentType {
  const descriptor = resolveComponentForMode(typeId, mode);
  return (descriptor && descriptor.component)
    ?
    descriptor.component :
    resolveComponentForMode(ControlType.NotImplemented, mode).component;
}

/**
 * Returns custom higher order components that a component should be wrapped in when connecting the UI
 * 
 * @param typeId
 * @param mode 
 */
export function resolveComponentEnhancers(typeId: number, mode: RenderMode) {
  const descriptor = resolveComponentForMode(typeId, mode);
  return (descriptor && descriptor.enhancers)
    ?
    descriptor.enhancers :
    resolveComponentForMode(ControlType.NotImplemented, mode).enhancers;
}

/**
 * The lightweight extra props seelctors
 */
export function resolveComponentSelectors(typeId: number, mode: RenderMode) {
  const descriptor = resolveComponentForMode(typeId, mode);
  return (descriptor && descriptor.selectors)
    ?
    descriptor.selectors :
    resolveComponentForMode(ControlType.NotImplemented, mode).selectors;
}

/**
 * Returns an array of event names that a component should have assigned
 */
export function resolveComponentEvents(typeId: number, mode: RenderMode) {
  const descriptor = resolveComponentForMode(typeId, mode);
  const hasModeEvents = descriptor && descriptor.events;
  const eventsForMode = hasModeEvents ? descriptor.events : [];
  const knownType = componentDefaults[typeId];
  if (!knownType) {
    return [];
  }
  return knownType.events.concat(eventsForMode);
}

/**
 * Returns an object of props that applies to the component independent of render mode
 */
export function resolveComponentDefaultProps(typeId: number) {
  return (componentDefaults[typeId] ? componentDefaults[typeId].props : undefined) || {};
}

export function resolveComponentIteration(typeId: number, mode: RenderMode) {
  const descriptor = resolveComponentForMode(typeId, mode);
  
  return descriptor && descriptor.iterate;
}
